import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import './MultipleChoice.css';

// Create audio element with public URL
const clickAudio = new Audio('/sounds/click.mp3');

// Preload the audio file
clickAudio.load();

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('MultipleChoice Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-message">
          <p>Something went wrong with this question.</p>
          <p>{this.state.error?.message}</p>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

const MultipleChoice = (props) => {
  const { 
    question, 
    selectedAnswers = [], 
    onAnswerChange = () => {}, 
    isSubmitted = false, 
    correctAnswers: propCorrectAnswers = [] 
  } = props;
  
  // Extract correct answers from options if not provided
  const correctAnswers = propCorrectAnswers.length > 0 
    ? propCorrectAnswers 
    : question.options
        .filter(option => option.isCorrect)
        .map(option => option.id);
  
  const [isMobile, setIsMobile] = useState(false);
  
  // Shuffle options while maintaining the original order for correct answer validation
  const shuffledOptions = useMemo(() => {
    if (!question?.options) return [];
    
    // Create a copy of the options array to shuffle
    const optionsToShuffle = [...question.options];
    
    // Fisher-Yates shuffle algorithm
    for (let i = optionsToShuffle.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [optionsToShuffle[i], optionsToShuffle[j]] = [optionsToShuffle[j], optionsToShuffle[i]];
    }
    
    return optionsToShuffle;
  }, [question?.options]);
  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Debug logging in development only
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('MultipleChoice Debug:', { 
        questionId: question?.id,
        selectedAnswers, 
        isSubmitted,
        correctAnswers,
        options: question?.options?.map(opt => ({
          id: opt.id, 
          isCorrect: opt.isCorrect
        }))
      });
    }
  }, [question, selectedAnswers, isSubmitted, correctAnswers]);

  // Validate props after hooks
  if (!question) {
    console.error('No question prop provided to MultipleChoice');
    return <div className="error-message">Error: No question data available</div>;
  }

  if (!question.options || !Array.isArray(question.options)) {
    console.error('Invalid or missing options in question:', question);
    return <div className="error-message">Error: Invalid question format</div>;
  }

  const handleOptionClick = (optionId) => {
    if (isSubmitted) return;
    
    if (question.multiple) {
      const newAnswers = selectedAnswers.includes(optionId)
        ? selectedAnswers.filter(id => id !== optionId)
        : [...selectedAnswers, optionId];
      onAnswerChange(newAnswers);
    } else {
      onAnswerChange([optionId]);
    }
    clickAudio.play();
  };

  // Helper function to compare IDs consistently
  const isIdInArray = (id, idArray) => {
    if (!idArray || !Array.isArray(idArray)) return false;
    return idArray.some(correctId => String(correctId) === String(id));
  };

  const getOptionClass = (optionId) => {
    const isSelected = isIdInArray(optionId, selectedAnswers);
    let className = 'option';
    
    if (isSelected) {
      className += ' selected';
    }
    
    // Only show correctness feedback if the question has been submitted
    if (isSubmitted && correctAnswers && correctAnswers.length > 0) {
      const isCorrect = isIdInArray(optionId, correctAnswers);
      if (isSelected) {
        // For selected options, show if they're correct or incorrect
        className += isCorrect ? ' correct' : ' incorrect';
      } else if (isCorrect) {
        // Show correct answers that weren't selected
        className += ' correct-not-selected';
      }
    }
    
    return className;
  };

  return (
    <ErrorBoundary>
      <div className="mcq-container">
        <div className={`options-grid ${isMobile ? 'mobile' : ''}`}>
          {shuffledOptions.map((option, index) => (
            <button
              key={option.id}
              className={getOptionClass(option.id)}
              onClick={() => handleOptionClick(option.id)}
              disabled={isSubmitted}
              aria-label={`Option ${index + 1}: ${option.text || 'Image option'}`}
            >
              {option.image ? (
                <div className="option-image-container">
                  <img 
                    src={option.image} 
                    alt="" 
                    className="option-image"
                    aria-hidden={!option.text}
                    aria-label={option.text ? undefined : 'Option image'}
                  />
                  {option.text && <span className="option-image-label">{option.text}</span>}
                </div>
              ) : (
                <span className={`option-text ${question.image ? '' : 'large'}`}>{option.text}</span>
              )}
            </button>
          ))}
        </div>

        {/* Feedback is handled by the parent Quiz component */}
      </div>
    </ErrorBoundary>
  );
};

MultipleChoice.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]).isRequired,
    text: PropTypes.string,
    image: PropTypes.string,
    multiple: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number
        ]).isRequired,
        text: PropTypes.string,
        image: PropTypes.string
      })
    ).isRequired
  }).isRequired,
  selectedAnswers: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ])
  ).isRequired,
  onAnswerChange: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool,
  correctAnswers: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ])
  )
};


export default MultipleChoice;
